import { MathUtils } from '@/math'
import { hexToHsl, hslToHex } from '@/utils/color'
import { type Position } from '@/voxelMath'

const DEFAULT_APPEARANCE = {
  surfaceY: 4,
  frame: {
    width: 5.6,
    height: 2,
    bevel: 0,
  },
  base: {
    visible: true,
    thickness: 0.35,
  },
  colors: {
    canvasTint: undefined as number | undefined,
    interior: 0xf4eee2,
    ground: 0xe8efec as number | undefined,
    grid: 0xbbcece as number | undefined,
  },
  canvas: {
    padding: { x: 64, z: 64 },
    extent: { x: 1200, z: 1200 },
  },
  grid: {
    thickness: 0.1,
    visible: true,
    opacity: 1,
  },
  baseGrid: {
    mode: 'full' as BoundsBaseGridMode,
  },
  border: {
    visible: false,
    strength: 1,
  },
  wallGrid: {
    mode: 'spotGrid' as BoundsWallGridMode,
  },
}

/**
 * Grid colours the start themes used before they were softened, keyed `ground:grid`, with the gentle
 * line that replaced each. The old lines were several times the floor's contrast and fought with what
 * was drawn on them; documents (and remembered home themes) made with an old preset now show the
 * softer line, a small step from the floor in the same hue.
 */
const SOFTENED_LEGACY_GRID_COLORS: ReadonlyMap<string, number> = new Map([
  ['e8efec:5f8890', 0xbbcece], // editor default
  ['d6dee6:405466', 0xb2bdc7], // studio
  ['102b46:6ee7ff', 0x1d445f], // blueprint
  ['e93a0f:921d00', 0xc02c08], // juicy
  ['5a2419:ff8a4a', 0x793722], // ember
  ['12383d:45d6b5', 0x1a5150], // aurora
  ['2d2856:ff9e80', 0x523d5d], // dusk
  ['069464:035a3d', 0x057952], // mint
  ['e2d3bf:6d5d4b', 0xc2b39f], // museum
])

const hex6 = (value: number) => (value & 0xffffff).toString(16).padStart(6, '0')

/** The softened grid colour for an old preset's ground/grid pair, or the grid colour unchanged. */
export function softenLegacyGridColor(ground: number, grid: number): number {
  return SOFTENED_LEGACY_GRID_COLORS.get(`${hex6(ground)}:${hex6(grid)}`) ?? grid
}

type AxisTuple = { x: number; z: number }
type FrameOptions = { width?: number; height?: number; bevel?: number }
type BaseOptions = { visible?: boolean; thickness?: number }
type GridOptions = { thickness?: number; visible?: boolean; opacity?: number }
type RidgeOptions = { width?: number; height?: number; embedRatio?: number }
export type BoundsGuideGridMode = 'spotlightGrid' | 'fullGrid'
type LegacyBoundsBaseGridMode = 'hoverSpotlight' | 'hoverFull'
type LegacyBoundsWallGridMode = BoundsGuideGridMode
export type BoundsBaseGridMode = 'off' | 'full' | 'spotGrid' | 'hoverGrid'
export type BoundsWallGridMode = 'off' | 'full' | 'spotGrid' | 'hoverGrid'
export type BoundsBaseGridOptions = {
  mode?: BoundsBaseGridMode | LegacyBoundsBaseGridMode
}
export type BoundsWallGridOptions = {
  mode?: BoundsWallGridMode | LegacyBoundsWallGridMode
}
export type BoundsBorderOptions = {
  visible?: boolean
  strength?: number
}
type BoundsColors = {
  canvasTint?: number
  interior?: number
  ground?: number
  grid?: number
  frame?: number
  ridge?: number
}

export type BoundsAppearanceOptions = {
  surfaceY?: number
  frame?: FrameOptions
  base?: BaseOptions
  ridge?: RidgeOptions
  colors?: BoundsColors
  canvas?: {
    padding?: number | Partial<AxisTuple>
    extent?: number | Partial<AxisTuple>
  }
  gridOpacity?: {
    ground?: number
    vertical?: number
  }
  grid?: GridOptions
  baseGrid?: BoundsBaseGridOptions
  border?: BoundsBorderOptions
  wallGrid?: BoundsWallGridOptions
}

export type ResolvedBoundsAppearance = {
  surfaceY: number
  frameWidth: number
  frameHeight: number
  frameBevel: number
  baseThickness: number
  canvasPadding: { x: number; z: number }
  canvasExtent: { x: number; z: number }
  colors: {
    canvasTint?: number
    interior: number
    ground: number
    grid: number
  }
  grid: {
    thickness: number
    visible: boolean
    opacity: number
  }
  baseGrid: {
    mode: BoundsBaseGridMode
  }
  border: {
    visible: boolean
    strength: number
  }
  wallGrid: {
    mode: BoundsWallGridMode
  }
  baseVisible: boolean
}

/** Grid and ground colours (hex) that read against a background: darker on light, lighter on dark. */
export function deriveSurfaceColors(backgroundHex: number): { gridHex: number; groundHex: number } {
  const hsl = hexToHsl(backgroundHex)

  const isLight = hsl.l > 60
  const gridDelta = isLight ? -45 : 45
  const groundDelta = isLight ? 45 : -45

  return {
    gridHex: hslToHex(hsl.h, hsl.s, hsl.l + gridDelta),
    groundHex: hslToHex(hsl.h, hsl.s, hsl.l + groundDelta),
  }
}

const toAxisTuple = (
  value: number | { x?: number; z?: number } | undefined,
  fallback: { x: number; z: number },
): { x: number; z: number } => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { x: value, z: value }
  }
  if (typeof value !== 'object' || value === null) return fallback
  return {
    x: value.x ?? fallback.x,
    z: value.z ?? fallback.z,
  }
}

export function mergeBoundsAppearanceOptions(
  base?: BoundsAppearanceOptions | null,
  override?: BoundsAppearanceOptions | null,
): BoundsAppearanceOptions | undefined {
  if (!base && !override) return undefined
  if (!base) return override ?? undefined
  if (!override) return base ?? undefined
  return {
    ...base,
    ...override,
    frame: {
      ...base.frame,
      ...override.frame,
    },
    base: {
      ...base.base,
      ...override.base,
    },
    ridge: {
      ...base.ridge,
      ...override.ridge,
    },
    colors: { ...base.colors, ...override.colors },
    canvas: { ...base.canvas, ...override.canvas },
    gridOpacity: override.gridOpacity ?? base.gridOpacity,
    grid: {
      ...base.grid,
      ...override.grid,
    },
    baseGrid: {
      ...base.baseGrid,
      ...override.baseGrid,
    },
    border: {
      ...base.border,
      ...override.border,
    },
    wallGrid: {
      ...base.wallGrid,
      ...override.wallGrid,
    },
  }
}

export function normalizeBoundsWallGridMode(mode: unknown): BoundsWallGridMode | undefined {
  if (mode === 'off' || mode === 'full' || mode === 'spotGrid' || mode === 'hoverGrid') return mode
  if (mode === 'spotlightGrid') return 'spotGrid'
  if (mode === 'fullGrid') return 'hoverGrid'
  return undefined
}

export function isBoundsBaseGridMode(mode: unknown): mode is BoundsBaseGridMode {
  return mode === 'off' || mode === 'full' || mode === 'spotGrid' || mode === 'hoverGrid'
}

export function normalizeBoundsBaseGridMode(mode: unknown): BoundsBaseGridMode | undefined {
  if (isBoundsBaseGridMode(mode)) return mode
  if (mode === 'hoverSpotlight') return 'spotGrid'
  if (mode === 'hoverFull') return 'hoverGrid'
  return undefined
}

function resolveWallGridMode(mode: BoundsWallGridOptions['mode'] | undefined): BoundsWallGridMode {
  return normalizeBoundsWallGridMode(mode) ?? DEFAULT_APPEARANCE.wallGrid.mode
}

function resolveBaseGridMode(
  mode: BoundsBaseGridOptions['mode'] | undefined,
  legacyVisible: boolean | undefined,
): BoundsBaseGridMode {
  const normalizedMode = normalizeBoundsBaseGridMode(mode)
  if (normalizedMode) return normalizedMode
  if (legacyVisible === false) return 'off'
  if (legacyVisible === true) return 'full'
  return DEFAULT_APPEARANCE.baseGrid.mode
}

export function baseGridHoverGuideMode(mode: BoundsBaseGridMode): BoundsGuideGridMode | null {
  if (mode === 'spotGrid') return 'spotlightGrid'
  if (mode === 'hoverGrid') return 'fullGrid'
  return null
}

export function baseGridFullGuideMode(mode: BoundsBaseGridMode): BoundsGuideGridMode | null {
  return mode === 'full' ? 'fullGrid' : null
}

export function wallGridHoverGuideMode(mode: BoundsWallGridMode): BoundsGuideGridMode | null {
  if (mode === 'spotGrid') return 'spotlightGrid'
  if (mode === 'hoverGrid') return 'fullGrid'
  return null
}

export function wallGridFullGuideMode(mode: BoundsWallGridMode): BoundsGuideGridMode | null {
  return mode === 'full' ? 'fullGrid' : null
}

function resolveGuideNumber(value: number | undefined, fallback: number, min: number, max: number): number {
  return MathUtils.clamp(
    typeof value === 'number' && Number.isFinite(value) ? value : fallback,
    min,
    max,
  )
}

function resolveNumber(value: number | undefined, fallback: number, min: number, max: number): number {
  return MathUtils.clamp(
    typeof value === 'number' && Number.isFinite(value) ? value : fallback,
    min,
    max,
  )
}

export function resolveBoundsAppearance({
  meta,
  backgroundHex,
  bounds,
}: {
  meta?: BoundsAppearanceOptions
  backgroundHex: number
  bounds: Position
}): ResolvedBoundsAppearance {
  const { gridHex: derivedGrid, groundHex: derivedGround } = deriveSurfaceColors(backgroundHex)
  const colors = meta?.colors ?? {}
  const canvasTint = typeof colors.canvasTint === 'number' && Number.isFinite(colors.canvasTint) ? colors.canvasTint : undefined
  const interiorHex = colors.interior ?? colors.ground ?? colors.frame ?? colors.ridge ?? DEFAULT_APPEARANCE.colors.interior
  const groundHex = colors.ground ?? DEFAULT_APPEARANCE.colors.ground ?? derivedGround
  const gridHex = softenLegacyGridColor(groundHex, colors.grid ?? DEFAULT_APPEARANCE.colors.grid ?? derivedGrid)

  const frameConfig = meta?.frame
  const ridgeConfig = meta?.ridge
  const frameWidth = Math.max(0, frameConfig?.width ?? ridgeConfig?.width ?? DEFAULT_APPEARANCE.frame.width)
  const frameHeight = frameConfig?.height ?? ridgeConfig?.height ?? Math.min(DEFAULT_APPEARANCE.frame.height, Math.max(bounds.y * 0.3, 0))
  const frameBevel = MathUtils.clamp(
    frameConfig?.bevel ?? ridgeConfig?.embedRatio ?? DEFAULT_APPEARANCE.frame.bevel,
    0,
    1,
  )
  const baseConfig = meta?.base
  const baseVisible = baseConfig?.visible ?? DEFAULT_APPEARANCE.base.visible
  const baseThickness = MathUtils.clamp(
    baseConfig?.thickness ?? DEFAULT_APPEARANCE.base.thickness,
    0.02,
    4,
  )
  const surfaceY = meta?.surfaceY ?? DEFAULT_APPEARANCE.surfaceY
  const canvasPadding = toAxisTuple(meta?.canvas?.padding, DEFAULT_APPEARANCE.canvas.padding)
  const canvasExtent = toAxisTuple(meta?.canvas?.extent, DEFAULT_APPEARANCE.canvas.extent)
  const gridOpacityGround = MathUtils.clamp(
    meta?.grid?.opacity ?? meta?.gridOpacity?.ground ?? DEFAULT_APPEARANCE.grid.opacity,
    0,
    1,
  )
  const gridThickness = resolveNumber(meta?.grid?.thickness, DEFAULT_APPEARANCE.grid.thickness, 0.002, 0.2)
  const baseGridMode = resolveBaseGridMode(meta?.baseGrid?.mode, meta?.grid?.visible)
  const gridVisible = baseGridMode !== 'off'
  const borderVisible = meta?.border?.visible ?? DEFAULT_APPEARANCE.border.visible
  const borderStrength = resolveGuideNumber(meta?.border?.strength, DEFAULT_APPEARANCE.border.strength, 0, 1)
  const wallGridMode = resolveWallGridMode(meta?.wallGrid?.mode)

  return {
    surfaceY,
    frameWidth,
    frameHeight,
    frameBevel,
    baseThickness,
    canvasPadding,
    canvasExtent,
    colors: {
      canvasTint,
      interior: interiorHex,
      ground: groundHex,
      grid: gridHex,
    },
    grid: {
      thickness: gridThickness,
      visible: gridVisible,
      opacity: gridOpacityGround,
    },
    baseGrid: {
      mode: baseGridMode,
    },
    border: {
      visible: borderVisible,
      strength: borderStrength,
    },
    wallGrid: {
      mode: wallGridMode,
    },
    baseVisible,
  }
}
